/**
 * Constantes y helpers del Panel General sin importar main_ui ni pantallas de módulo.
 * Evita ciclos: main_ui → modulo_* → Dashboard → ModuleGeneralPanel → main_ui.
 */
import { isPlatformDesignerUser } from "@/lib/usePlatformDesigner";

export const STORAGE_KEY = "vf:active_work_module";

export const WORK_MODULES = {
  VISOR_SAT: "visor_sat",
  ASISTENTE_LABORAL: "asistente_laboral",
  TRAMITES_FISCALES: "tramites_fiscales",
  GESTION_DESPACHO: "gestion_despacho",
  MATERIALIDAD: "materialidad",
  ANALISIS_FINANCIERO: "analisis_financiero",
  DISENADOR: "disenador",
};

/** @deprecated Use WORK_MODULES.TRAMITES_FISCALES — alias de compatibilidad. */
export const HUB_TRAMITES = WORK_MODULES.TRAMITES_FISCALES;

const VALID_MODULE_IDS = new Set(Object.values(WORK_MODULES));

/** Nombre corto de la plataforma (login, sidebar, textos generales). */
export const VISOR_MODULE_DISPLAY_NAME = "BNT Gestión y Visor Fiscal";

/** Título del módulo Visor (hub inicio, h1, breadcrumbs de contexto). */
export const VISOR_MODULE_CARD_TITLE = "Visor Fiscal CFDI";

/** Primer segmento de breadcrumb en pantallas del módulo Visor SAT. */
export const VISOR_MODULE_BREADCRUMB_ROOT = VISOR_MODULE_CARD_TITLE;

/** Tarjeta hub, sidebar, título y breadcrumb de /conciliacion-manual-cfdi. */
export const CONCILIACION_MANUAL_CFDI_TITLE =
  "Conciliación manual de CFDI (Sistema / Excel) para Confronta";

/** Pestaña del navegador cuando el usuario opera en el módulo Visor SAT. */
export const VISOR_MODULE_BROWSER_TITLE = `${VISOR_MODULE_CARD_TITLE} — Contabilidad y confronta SAT`;

export const MODULE_CARDS = {
  [WORK_MODULES.VISOR_SAT]: {
    id: WORK_MODULES.VISOR_SAT,
    title: VISOR_MODULE_CARD_TITLE,
    description: "Operación CFDI por cliente: carga, consulta, confronta contable y cumplimiento fiscal ante el SAT.",
    bullets: [
      "Carga y clasificación de XML CFDI",
      "Visor y consulta por UUID y periodo",
      "Confronta SAT vs contabilidad (Excel)",
      "Sincronización automática y rescate manual con e.Firma",
      "Auditoría EFOS y alertas de riesgo",
    ],
    entryPath: "/visor/inicio",
  },
  [WORK_MODULES.ASISTENTE_LABORAL]: {
    id: WORK_MODULES.ASISTENTE_LABORAL,
    title: "Asistente Laboral / REPSE",
    description: "Expediente laboral, validaciones IMSS/SAT, inscripciones y control REPSE por cliente de trabajo.",
    bullets: [
      "Registros patronales y empresas",
      "Catálogo y expediente de trabajadores",
      "Validación NSS, RFC y CURP",
      "Inscripciones y bajas IMSS",
      "Control REPSE y reportes de emisión",
    ],
    /** Ruta operativa al entrar desde el Panel General (hub). */
    entryPath: "/laboral/inicio",
    path: "/laboral/inicio",
    route: "/laboral/inicio",
  },
  [WORK_MODULES.TRAMITES_FISCALES]: {
    id: WORK_MODULES.TRAMITES_FISCALES,
    title: "Trámites Fiscales",
    description: "Validación de contribuyentes, altas SAT/IMSS, consulta NSS y descargas fiscales e institucionales por cliente.",
    bullets: [
      "Constancia de situación fiscal y opinión de cumplimiento (SAT)",
      "Validación masiva de RFC / CURP",
      "Inscripción de trabajadores en portal SAT",
      "Validador NSS y movimientos IMSS (IDSE)",
      "Descargas IMSS e INFONAVIT por periodo",
    ],
    entryPath: "/tramites/inicio",
    path: "/tramites-fiscales",
  },
  [WORK_MODULES.GESTION_DESPACHO]: {
    id: WORK_MODULES.GESTION_DESPACHO,
    title: "Gestión Despacho",
    description: "Operación avanzada del despacho: clientes operativos, tareas, asignaciones y atención WhatsApp.",
    bullets: [
      "Cartera de clientes operativos y asignación a auxiliares",
      "Tareas con vencimiento, prioridad y recordatorios",
      "Calendario de pendientes por cliente y equipo",
      "Atención a clientes tipo WhatsApp Business (bandeja compartida)",
      "Reportes de carga de trabajo y productividad",
    ],
    entryPath: "/despacho/inicio",
    path: "/despacho/inicio",
    route: "/despacho/inicio",
  },
  [WORK_MODULES.MATERIALIDAD]: {
    id: WORK_MODULES.MATERIALIDAD,
    title: "Expedientes de operaciones",
    description:
      "Soporte documental: revisión del despacho, portal de clientes, detección CFDI, riesgos EFOS/opinión y exportación PDF.",
    bullets: [
      "Revisión del despacho: lista de verificación por empresa, periodo y auxiliar",
      "Detección CFDI (PPD, cancelados, proveedor nuevo)",
      "Riesgos EFOS / opinión de cumplimiento",
      "Exportación de expediente PDF",
    ],
    entryPath: "/materialidad/expediente",
    path: "/materialidad/expediente",
    route: "/materialidad/expediente",
  },
  [WORK_MODULES.ANALISIS_FINANCIERO]: {
    id: WORK_MODULES.ANALISIS_FINANCIERO,
    title: "Análisis financiero",
    description:
      "Gastos razonables y proyección al cierre a partir del estado de resultados de cada empresa. Incluido para todos los usuarios; no se vende aparte.",
    bullets: [
      "Carga del estado de resultados en Excel, PDF con texto o captura manual",
      "Tabla de referencias editable, marcada como ejemplo",
      "Proyección al cierre (cálculo en la siguiente fase)",
    ],
    entryPath: "/analisis-financiero",
    path: "/analisis-financiero",
    route: "/analisis-financiero",
  },
  [WORK_MODULES.DISENADOR]: {
    id: WORK_MODULES.DISENADOR,
    title: "Módulo Maestro Diseñador",
    description:
      "Emisión comercial, Centro de Licencias BNT y herramientas técnicas de soporte.",
    bullets: [
      "Catálogo y cartera de licencias",
      "Centro de Licencias BNT (SaaS)",
      "Herramientas técnicas y monitor SAT",
    ],
    entryPath: "/disenador/inicio",
  },
};

export const MODULE_CONTEXT_LABELS = {
  [WORK_MODULES.VISOR_SAT]: VISOR_MODULE_CARD_TITLE,
  [WORK_MODULES.ASISTENTE_LABORAL]: "Asistente Laboral y REPSE",
  [WORK_MODULES.TRAMITES_FISCALES]: "Trámites Fiscales",
  [WORK_MODULES.GESTION_DESPACHO]: "Gestión Despacho",
  [WORK_MODULES.MATERIALIDAD]: "Expedientes de operaciones",
  [WORK_MODULES.ANALISIS_FINANCIERO]: "Análisis financiero",
  [WORK_MODULES.DISENADOR]: "Módulo Maestro Diseñador",
};

export const PREMIUM_LABOR_LOCKED_MESSAGE =
  "Este módulo Premium requiere una licencia activa. Puede solicitar su cotización y activación " +
  "en la sección de Licencias mediante el botón «Solicitar Licencia».";

export function isDesignerUser(user) {
  return isPlatformDesignerUser(user);
}

export function getStoredWorkModule() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && VALID_MODULE_IDS.has(v)) return v;
    return WORK_MODULES.VISOR_SAT;
  } catch {
    return WORK_MODULES.VISOR_SAT;
  }
}

export function setStoredWorkModule(moduleId) {
  try {
    const id = VALID_MODULE_IDS.has(moduleId) ? moduleId : WORK_MODULES.VISOR_SAT;
    localStorage.setItem(STORAGE_KEY, id);
    window.dispatchEvent(new CustomEvent("visor:work-module-changed", { detail: { moduleId: id } }));
  } catch {
    /* localStorage no disponible */
  }
}

export function getPanelCardsForUser({ isPlatformDesigner = false } = {}) {
  const base = [
    WORK_MODULES.VISOR_SAT,
    WORK_MODULES.GESTION_DESPACHO,
    WORK_MODULES.TRAMITES_FISCALES,
    WORK_MODULES.MATERIALIDAD,
    WORK_MODULES.ASISTENTE_LABORAL,
    WORK_MODULES.ANALISIS_FINANCIERO,
  ];
  if (isPlatformDesigner) {
    return [...base, WORK_MODULES.DISENADOR];
  }
  return base;
}

/** @deprecated Use getPanelCardsForUser({ isPlatformDesigner }) */
export function getHubCardsForUser(opts) {
  return getPanelCardsForUser(opts);
}

export function moduleContextLabel(moduleId) {
  return MODULE_CONTEXT_LABELS[moduleId] || MODULE_CONTEXT_LABELS[WORK_MODULES.VISOR_SAT];
}

/** Ruta operativa al entrar a un módulo desde el hub (no confundir con Panel General `/`). */
export function getModuleEntryPath(moduleId) {
  const meta = MODULE_CARDS[moduleId];
  return meta?.entryPath || meta?.path || meta?.route || "/";
}

/**
 * Hub de tarjetas del módulo activo (inicio interno). Nunca apunta al Panel General `/`.
 * @param {string} moduleId - WORK_MODULES.*
 */
export function getModuleInicioPath(moduleId) {
  if (moduleId === WORK_MODULES.ASISTENTE_LABORAL) return "/laboral/inicio";
  if (moduleId === WORK_MODULES.TRAMITES_FISCALES) return "/tramites/inicio";
  if (moduleId === WORK_MODULES.GESTION_DESPACHO) return "/despacho/inicio";
  if (moduleId === WORK_MODULES.MATERIALIDAD) return "/materialidad/expediente";
  if (moduleId === WORK_MODULES.ANALISIS_FINANCIERO) return "/analisis-financiero";
  if (moduleId === WORK_MODULES.DISENADOR) return "/disenador/inicio";
  return "/visor/inicio";
}

/** Panel General del módulo activo (ruta raíz). */
export function defaultRouteForModule() {
  return "/";
}
